import { groupLabel } from "./access-group-labels";
import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { AccessRoleImpactDocument } from "@/graphql/generated/operations";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { AccessImpactPeople } from "./AccessImpactPeople";
import { AccessPermissionDescription } from "./AccessPermissionDescription";
import { userErrorMessage } from "@/lib/user-error";

export function AccessRoleImpactDialog({ roleId, permissionIds, onConfirm, onCancel }: {
  roleId: string; permissionIds: string[]; onConfirm: () => void; onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [groupsAfter, setGroupsAfter] = useState<string | null>(null);
  const [peopleAfter, setPeopleAfter] = useState<string | null>(null);
  const { data, loading, error } = useQuery(AccessRoleImpactDocument, {
    variables: { roleId, permissionIds, groupsAfter, peopleAfter }, fetchPolicy: "network-only",
  });
  const impact = data?.accessRoleImpact;
  const [peopleOpen, setPeopleOpen] = useState(false);
  return <Dialog open id="roles-impact" dismiss="explicit" title={t("admin.roles.impactTitle")} onClose={onCancel}>
    <div className="space-y-4 text-smycles-body text-ink">
      <p>{t("admin.roles.trustedAdmin")}</p>
      <p>{t("admin.roles.impactHint")}</p>
      {loading && <p role="status">{t("admin.roles.loading")}</p>}
      {error && <p role="alert">{userErrorMessage(intl, error)}</p>}
      {impact && !loading && !error && <>
        {!impact.canSave && <p role="alert">{t("admin.roles.impactIncompatible")}</p>}
        {[{ label: "admin.roles.impactAdded", permissions: impact.addedPermissions },
          { label: "admin.roles.impactRemoved", permissions: impact.removedPermissions }].map((section) =>
          <section key={section.label}>
            <h3 className="font-medium">{t(section.label)}</h3>
            {section.permissions.length ? <ul>{section.permissions.map((permission) =>
              <li key={permission.id} className="break-words"><AccessPermissionDescription code={permission.code} description={permission.description} /></li>)}</ul>
              : <p>{t("admin.roles.impactNone")}</p>}
          </section>)}
        <section>
          <h3 className="font-medium">{t("admin.roles.impactGroups")}</h3>
          {impact.groups.length ? <ul>{impact.groups.map((group) => <li key={group.id}>{groupLabel(intl, group)}</li>)}</ul>
            : <p>{t("admin.roles.impactNone")}</p>}
          {impact.groupsHasNextPage && <Button variant="ghost" onClick={() => setGroupsAfter(impact.groupsNextAfter ?? null)}>
            {t("admin.roles.impactNext")}</Button>}
        </section>
        <Disclosure id="roles-impact-people" open={peopleOpen} onToggle={() => setPeopleOpen((open) => !open)} title={t("admin.roles.impactPeople")} count={`${impact.personIds.length}${impact.peopleHasNextPage ? "+" : ""}`}>
          <p className="text-smycles-meta">{t("admin.roles.impactPeopleHint")}</p>
          {peopleOpen && <AccessImpactPeople personIds={impact.personIds} />}
          {impact.peopleHasNextPage && <Button variant="ghost" onClick={() => setPeopleAfter(impact.peopleNextAfter ?? null)}>
            {t("admin.roles.impactNext")}</Button>}
        </Disclosure>
      </>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button id="roles-impact-confirm" disabled={loading || !!error || !impact?.canSave} onClick={onConfirm}>
          {t("admin.roles.impactSave")}</Button>
      </div>
    </div>
  </Dialog>;
}
